import { useEffect } from "react";
import ReactDOM from "react-dom/client";
import { MsalProvider } from "@azure/msal-react";
import {
  acquireCoreToken,
  acquireAlertToken,
  acquireSubscriptionToken,
  handleLinkingRedirectCallback,
  initializeAuthInstances,
  msalInstance,
} from "./utils/auth/index.ts";
import { AlertingAuthScope, AlertServiceProvider } from "@gf-alerting/widgets";
import Navigation from "./components/Navigation";
import { QueryClientProvider } from "@tanstack/react-query";
import queryClient from "./app/queryClient.ts";
import Root from "./components/Root/Root.tsx";
import ThemeHOC from "./providers/ThemeHOC.tsx";
import { I18nextProvider } from "react-i18next";
import translation from "./localization/i18next";
import { APP_VERSION } from "./utils/version";
import { StyledToastContainer } from "./main.styles.ts";
import { dark, light } from "@gds-react/core";
import { useStore } from "./store/store.ts";
import CoreProvider from "@gf-core/widgets/providers/CoreProvider";
import { CoreAuthScope } from "@gf-core/widgets/types/CoreAuthScope";
import { CoreApplicationContextEnum } from "@gf-core/widgets/types/CoreApplicationContextEnum";
import {
  ServicePortalAuthScope,
  ServicePortalProvider,
} from "@gf-ww-service-portal/widgets";

// Entry module renders directly and has no exports, so fast refresh does not apply.
// eslint-disable-next-line react-refresh/only-export-components
function App() {
  const theme = useStore.useTheme();
  const { iso639Code } = useStore.useLanguageSettings();

  useEffect(() => {
    window.__APP_VERSION__ = APP_VERSION;
  }, []);

  return (
    <I18nextProvider i18n={translation}>
      <QueryClientProvider client={queryClient}>
        <MsalProvider instance={msalInstance}>
          <ThemeHOC>
            <Root>
              <CoreProvider
                getAccessToken={async (scopes) => {
                  const accessToken = await acquireCoreToken(scopes);
                  return accessToken;
                }}
                environment={import.meta.env.VITE_ENVIRONMENT}
                authScope={CoreAuthScope.B2C}
                applicationContext={CoreApplicationContextEnum.DMW}
              >
                <AlertServiceProvider
                  tokens={theme === "dark" ? dark : light}
                  environment={import.meta.env.VITE_ENVIRONMENT}
                  authScope={AlertingAuthScope.B2C}
                  getAccessToken={async (scopes) => {
                    const accessToken = await acquireAlertToken(scopes);

                    return accessToken;
                  }}
                  locale={iso639Code}
                >
                  <ServicePortalProvider
                    getAccessToken={async (scopes) => {
                      const accessToken =
                        await acquireSubscriptionToken(scopes);
                      return accessToken;
                    }}
                    authScope={ServicePortalAuthScope.B2C}
                    environment={import.meta.env.VITE_ENVIRONMENT}
                    userLanguage={iso639Code}
                  >
                    <StyledToastContainer />
                    <Navigation />
                  </ServicePortalProvider>
                </AlertServiceProvider>
              </CoreProvider>
            </Root>
          </ThemeHOC>
        </MsalProvider>
      </QueryClientProvider>
    </I18nextProvider>
  );
}

initializeAuthInstances().then(async () => {
  await handleLinkingRedirectCallback();
  ReactDOM.createRoot(document.getElementById("root")!).render(<App />);
});
